import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/AuthContext.jsx";
import { alertV2 } from "../../../api/index.js";

function AlarmActionModal({
  show,
  onHide,
  alarmId,
  onSuccess,
  alarmStatus
}) {
  const { email } = useAuth();
  const [selectedAction, setSelectedAction] = useState("");
  const [formData, setFormData] = useState({
    // Monitor & Wait
    monitorDate: "",
    monitorDetails: "",
    
    // Proceed with Maintenance
    maintenanceDate: "",
    maintenanceDetails: "",
    engineersCount: 1,
    
    // Maintenance Completed
    isValid: "",
    maintenanceHours: "",
    completedEngineersCount: 1,
    maintenanceCost: "",
    maintenanceCurrency: "USD",
    unplannedDowntimeAvoided: "",
    manHoursSaved: ""
  });

  useEffect(() => {
    if (show) {
      setSelectedAction("");
      setFormData({
        monitorDate: "",
        monitorDetails: "",
        maintenanceDate: "",
        maintenanceDetails: "",
        engineersCount: 1,
        isValid: "",
        maintenanceHours: "",
        completedEngineersCount: 1,
        maintenanceCost: "",
        maintenanceCurrency: "USD",
        unplannedDowntimeAvoided: "",
        manHoursSaved: ""
      });
    }
  }, [show]);

  // Calculate man-hours saved automatically
  useEffect(() => {
    if (formData.completedEngineersCount && formData.unplannedDowntimeAvoided) {
      const manHours = formData.completedEngineersCount * formData.unplannedDowntimeAvoided;
      setFormData(prev => ({ ...prev, manHoursSaved: manHours }));
    }
  }, [formData.completedEngineersCount, formData.unplannedDowntimeAvoided]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
          let payload = generatedPayload();

      await alertV2.addLogAction(alarmId, payload, null);
      toast.success("Alarm action updated successfully!");
      onSuccess();
      onHide();
    } catch (error) {
      console.error("Error updating alarm action:", error);
      toast.error("Failed to update alarm action");
    }
  };

  const generatedPayload = () => {
    if (selectedAction == 'Monitor & Wait') {
      return  {
        email: email,
        event_details: `Has responded to the alarm with action: ${selectedAction}`,
        event_name: "Response",
        event_action: selectedAction,
        alert_id: alarmId,
        action_detail:  JSON.stringify({
          action_completed     : `wait until ${formData.monitorDate}`,
          confirmed_issue      : "",
          action_work          : selectedAction + ' until ' + formData.monitorDate,
          action_name          : selectedAction,
          additional_comment   : formData.monitorDetails,
          action_date          : new Date(formData.monitorDate),
        }),
      }
         
    } else if (selectedAction == 'Proceed with Maintenance') {
      return {
        email: email,
        event_details: `Has responded to the alarm with action: ${selectedAction}`,
        event_name: "Response",
        event_action: selectedAction,
        alert_id: alarmId,
        action_detail:  JSON.stringify({
          action_completed     : `maintenance at ${formData.maintenanceDate}`,
          confirmed_issue      : "",
          action_work          : selectedAction + ' at ' + formData.maintenanceDate,
          action_name          : selectedAction,
          additional_comment   : formData.maintenanceDetails,
          action_date          : new Date(formData.maintenanceDate),
          total_engineers      : formData.engineersCount,
        }),
      };
    } else if (selectedAction == 'Closed') {
      return {
        email: email,
        event_details: `This is ${formData.isValid} Alert`,
        event_name: "Response",
        event_action: formData.isValid === "valid" ? "Closed" : "Invalid",
        alert_id: alarmId,
        action_detail:  JSON.stringify({
          action_completed     : formData.isValid === "valid" ? "action completed" : "action invalid",
          confirmed_issue      : "",
          action_work          : formData.isValid === "valid" ? "action completed" : "action invalid",
          action_name          : formData.isValid === "valid" ? "Closed" : "Invalid",
          action_date          : new Date(),
          total_engineers      : formData.completedEngineersCount,
          maintenance_hours    : formData.maintenanceHours || 0,
          maintenance_currency : formData.maintenanceCurrency,
          unplanned_downtime   : String(formData.unplannedDowntimeAvoided),
          man_hour_saved       : String(formData.manHoursSaved),
          maintenance_cost     : formData.maintenanceCost || 0,
          additional_comment   : "",
        }),
      };
    }
  };

  const renderMonitorWaitForm = () => (
    <div>
      <div className="mb-3">
        <label className="form-label fw-bold">Monitor until:</label>
        <Form.Control
          type="date"
          value={formData.monitorDate}
          onChange={(e) => handleInputChange("monitorDate", e.target.value)}
          required
        />
        <small className="text-muted">
          You will receive notifications (Email & WhatsApp) when this date arrives
        </small>
      </div>
      <div className="mb-3">
        <label className="form-label fw-bold">Details:</label>
        <Form.Control
          as="textarea"
          rows="3"
          value={formData.monitorDetails}
          onChange={(e) => handleInputChange("monitorDetails", e.target.value)}
          placeholder="Please explain why you want to monitor & wait..."
          required
        />
      </div>
    </div>
  );

  const renderProceedMaintenanceForm = () => (
    <div>
      <div className="mb-3">
        <label className="form-label fw-bold">Date for Maintenance:</label>
        <Form.Control
          type="date"
          value={formData.maintenanceDate}
          onChange={(e) => handleInputChange("maintenanceDate", e.target.value)}
          required
        />
        <small className="text-muted">
          You will receive notifications (Email & WhatsApp) when this date arrives
        </small>
      </div>
      <div className="mb-3">
        <label className="form-label fw-bold">Details:</label>
        <Form.Control
          as="textarea"
          rows="3"
          value={formData.maintenanceDetails}
          onChange={(e) => handleInputChange("maintenanceDetails", e.target.value)}
          placeholder="Please explain why you want to proceed with maintenance..."
          required
        />
      </div>
      <div className="mb-3">
        <label className="form-label fw-bold">No. Engineers:</label>
        <Form.Control
          type="number"
          min="1"
          value={formData.engineersCount}
          onChange={(e) => handleInputChange("engineersCount", parseInt(e.target.value))}
          required
        />
      </div>
    </div>
  );

  const renderMaintenanceCompletedForm = () => (
    <div>
      <div className="mb-3">
        <label className="form-label fw-bold">Valid or Invalid:</label>
        <Form.Select
          value={formData.isValid}
          onChange={(e) => handleInputChange("isValid", e.target.value)}
          required
        >
          <option value="">Select option</option>
          <option value="valid">Valid</option>
          <option value="invalid">Invalid</option>
        </Form.Select>
      </div>
      
      {formData.isValid === "valid" && (
        <>
          <div className="mb-3">
            <label className="form-label fw-bold">Hours of Maintenance:</label>
            <Form.Control
              type="number"
              min="0"
              step="0.5"
              value={formData.maintenanceHours}
              onChange={(e) => handleInputChange("maintenanceHours", parseFloat(e.target.value))}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">No. Engineers:</label>
            <Form.Control
              type="number"
              min="1"
              value={formData.completedEngineersCount}
              onChange={(e) => handleInputChange("completedEngineersCount", parseInt(e.target.value))}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Maintenance Cost:</label>
            <div className="d-flex gap-2">
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={formData.maintenanceCost}
                onChange={(e) => handleInputChange("maintenanceCost", parseFloat(e.target.value))}
                placeholder="0.00"
                required
              />
              <Form.Select
                value={formData.maintenanceCurrency}
                onChange={(e) => handleInputChange("maintenanceCurrency", e.target.value)}
                style={{ width: "100px" }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="IDR">IDR</option>
              </Form.Select>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Unplanned Downtime Avoided (hours):</label>
            <Form.Control
              type="number"
              min="0"
              step="0.5"
              value={formData.unplannedDowntimeAvoided}
              onChange={(e) => handleInputChange("unplannedDowntimeAvoided", parseFloat(e.target.value))}
              placeholder="If machine broke down, how many hours to resolve it"
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Man-Hours Saved:</label>
            <Form.Control
              type="number"
              value={formData.manHoursSaved}
              disabled
           
            />
            <small className="text-muted">
              Automatically calculated: No. Engineers × Unplanned Downtime Avoided
            </small>
          </div>
        </>
      )}
    </div>
  );

  const renderForm = () => {
    switch (selectedAction) {
      case "Monitor & Wait":
        return renderMonitorWaitForm();
      case "Proceed with Maintenance":
        return renderProceedMaintenanceForm();
      case "Closed":
        return renderMaintenanceCompletedForm();
      default:
        return null;
    }
  };

  const isFormValid = () => {
    if (!selectedAction) return false;
    
    switch (selectedAction) {
      case "Monitor & Wait":
        return formData.monitorDate && formData.monitorDetails;
      case "Proceed with Maintenance":
        return formData.maintenanceDate && formData.maintenanceDetails && formData.engineersCount;
      case "Closed":
        if (formData.isValid !== "valid") return true;
        return formData.maintenanceHours && formData.completedEngineersCount && 
               formData.maintenanceCost && formData.unplannedDowntimeAvoided;
      default:
        return false;
    }
  };

  return (
    <Modal show={show} size="lg" onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Alarm Action Selection</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!selectedAction ? (
          <div className="d-grid gap-3">
            <Button
              variant="outline-primary"
              size="lg"
              onClick={() => setSelectedAction("Monitor & Wait")}
              className="text-start p-3"
            >
              <div className="fw-bold">1. Monitor & Wait</div>
              <small className="text-muted">
                Select this option if you want to monitor the situation and wait for further developments
              </small>
            </Button>
            
            <Button
              variant="outline-warning"
              size="lg"
              onClick={() => setSelectedAction("Proceed with Maintenance")}
              className="text-start p-3"
            >
              <div className="fw-bold">2. Proceed with Maintenance</div>
              <small className="text-muted">
                Select this option if you want to proceed with maintenance activities
              </small>
            </Button>
            
            <Button
              variant="outline-success"
              size="lg"
              onClick={() => setSelectedAction("Closed")}
              className="text-start p-3"
            >
              <div className="fw-bold">3. Maintenance Completed</div>
              <small className="text-muted">
                Select this option if maintenance has been completed
              </small>
            </Button>
          </div>
        ) : (
          <div>
            <div className="mb-3">
              <Button
                variant="link"
                onClick={() => setSelectedAction("")}
                className="p-0 text-decoration-none"
              >
                ← Back to action selection
              </Button>
            </div>
            <h5 className="mb-3">{selectedAction}</h5>
            {renderForm()}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        {selectedAction && (
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            Submit
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AlarmActionModal;
